import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import FinanceLegalNote from "@/components/FinanceLegalNote";
import { useLang } from "@/context/LanguageContext";
import { Slider } from "@/components/ui/slider";
import useSeo from "@/hooks/useSeo";
import { Percent, Wallet, CalendarClock, Zap, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1554744512-d6c603f27c54?q=80&w=2000";
const RATE = 4.9;
const ICONS = [Percent, Wallet, CalendarClock, Zap];

const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Finanzierung() {
  const { t } = useLang();
  useSeo({
    title: "Auto-Finanzierung Köln ab 4,90 % · Individuell & flexibel | DNA Automotive",
    description: "Fahrzeug-Finanzierung in Köln ab 4,90 % über starke Partner wie Santander & Bank11. Ohne Anzahlung möglich, individuelle Laufzeiten. Jetzt Rate berechnen.",
    path: "/finanzierung",
    breadcrumbs: [{ name: "Finanzierung", url: "/finanzierung" }],
  });
  useEffect(() => window.scrollTo(0, 0), []);
  const [amount, setAmount] = useState(50000);
  const [down, setDown] = useState(5000);
  const [term, setTerm] = useState(60);

  const monthly = useMemo(() => {
    const principal = Math.max(0, amount - down);
    const r = RATE / 100 / 12;
    const n = term;
    if (principal <= 0) return 0;
    if (r === 0) return principal / n;
    return (principal * r) / (1 - Math.pow(1 + r, -n));
  }, [amount, down, term]);

  const scrollContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-obsidian min-h-screen" data-testid="finanzierung-page">
      <Header />
      <section className="relative pt-40 pb-24 px-6 md:px-10 overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover grayscale opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/60" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.finance.overline}</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl leading-[0.9] mb-6">{t.finance.title}</h1>
          <p className="text-zinc-300 max-w-2xl text-lg">{t.finance.subtitle}</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 md:px-10 pb-8">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.finance.benefits.map((b, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-obsidian-surface border border-white/10 p-7 hover:border-brand-light/50 transition-colors duration-500"
                data-testid={`finance-benefit-${i}`}
              >
                <Icon className="w-7 h-7 text-brand-bright mb-4" />
                <div className="font-heading font-black text-white text-2xl mb-2">{b.t}</div>
                <p className="text-zinc-400 text-sm leading-relaxed">{b.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Calculator */}
      <section className="px-6 md:px-10 py-20">
        <div className="max-w-[1000px] mx-auto bg-obsidian-surface border border-white/10 p-8 md:p-12" data-testid="finance-calculator">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl mb-10">{t.finance.calc.title}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-10">
              <div>
                <div className="flex justify-between mb-4">
                  <span className="text-sm uppercase tracking-wider text-zinc-400">{t.finance.calc.amount}</span>
                  <span className="font-heading font-bold text-brand-bright" data-testid="calc-amount">{fmt(amount)}</span>
                </div>
                <Slider value={[amount]} min={10000} max={250000} step={1000} onValueChange={(v) => setAmount(v[0])} />
              </div>
              <div>
                <div className="flex justify-between mb-4">
                  <span className="text-sm uppercase tracking-wider text-zinc-400">{t.finance.calc.down}</span>
                  <span className="font-heading font-bold text-brand-bright" data-testid="calc-down">{fmt(down)}</span>
                </div>
                <Slider value={[down]} min={0} max={Math.max(0, amount - 1000)} step={500} onValueChange={(v) => setDown(v[0])} />
              </div>
              <div>
                <div className="flex justify-between mb-4">
                  <span className="text-sm uppercase tracking-wider text-zinc-400">{t.finance.calc.term}</span>
                  <span className="font-heading font-bold text-brand-bright" data-testid="calc-term">{term} {t.finance.calc.months}</span>
                </div>
                <Slider value={[term]} min={12} max={120} step={6} onValueChange={(v) => setTerm(v[0])} />
              </div>
            </div>

            <div className="flex flex-col justify-center items-center text-center bg-obsidian border border-brand-light/30 p-8">
              <span className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">{t.finance.calc.monthly}</span>
              <div className="font-heading font-black text-brand-bright text-5xl md:text-6xl mb-3" data-testid="calc-monthly">
                {fmt(monthly)}
              </div>
              <span className="text-sm text-zinc-500 mb-6">{t.finance.calc.rate}: {RATE.toFixed(2).replace(".", ",")} %</span>
              <button onClick={scrollContact} className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-brand-hover transition-colors" data-testid="finance-cta">
                {t.finance.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-8">{t.finance.calc.disclaimer}</p>
          <FinanceLegalNote className="mt-3" />
        </div>
      </section>

      {/* Partners */}
      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-[1600px] mx-auto text-center">
          <h3 className="text-xs uppercase tracking-[0.3em] text-brand-bright mb-10">{t.finance.partnersTitle}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Santander Bank", logo: "/partners/santander.png" },
              { name: "Bank11", logo: "/partners/bank11.png" },
              { name: "Creditreform", logo: "/partners/creditreform.png" },
            ].map((p) => (
              <div key={p.name} className="flex items-center justify-center bg-white py-10 px-8 border border-white/10">
                <img src={p.logo} alt={p.name} className="h-10 md:h-11 w-auto object-contain" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection prefillVehicle={t.finance.title} />
      <Footer />
      <WhatsAppButton prefill={`Hallo DNA Automotive, ich interessiere mich für eine ${t.finance.title}.`} />
    </div>
  );
}
